import React, {FC, useEffect, useMemo, useState} from 'react'
import {useFieldArray, useFormContext, useWatch} from 'react-hook-form'
import DeleteIcon from '@pixel/assets/icons/Delete'
import IconButton from '@pixel/core/IconButton'
import Button from '@pixel/core/Button'
import { styled } from '@pixel/core/styles'
import ConditionInput from './ConditionInput'
import { CircularProgress, Typography } from '@mui/material'
import LocalizedTypography from '../../components/LocalizedTypography'
import { LocalizedText } from '../../contexts/Localization/LocalizationTypes'
import useAutomationFormDataQuery from '../hooks/useAutomationFormDataQuery'
import {
    useLocalizedTextArray,
    useMultipleLocalizedText
} from "../../contexts/Localization/localizationHooks";
import {AutomationSearchResult} from "../types/Automations";
import {useRequiredFilterFields} from "../hooks/useRequiredFilterFields";

const ConditionContainer = styled('div')(({ theme }) => ({
    display: 'flex',
    marginTop: theme.spacing(1),
    maxWidth: '570px'
}))

const AddConditionContainer = styled('div')(({ theme }) => ({
    display: 'flex',
    justifyContent: 'end',
    marginTop: theme.spacing(1),
}))

const AndContainer = styled('div')(({ theme }) => ({
    display: 'flex',
    marginTop: theme.spacing(1),
}))

const ConditionLoadingContainer = styled('div')(({ theme }) => ({
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(1),
}))

interface ConditionListSectionProps {
    selectedAutomation: AutomationSearchResult,
    errors: any
    setErrors: any,
}

const ConditionListSection: FC<ConditionListSectionProps> = ({
   selectedAutomation,
   errors,
   setErrors
}) => {
    const { control, formState, setValue, resetField } = useFormContext()

    const { fields, append, remove, } = useFieldArray({
        control,
        name: 'companyFilters',
        rules: {
            minLength: 1,
            maxLength: 5,
        }
    })

    const automationType = useWatch({ name: 'automationType', control })
    const selectedTrigger = useWatch({ name: `triggerType`, control })
    const companyFilters = useWatch({ name: `companyFilters`, control })
    const filteredFields = useRequiredFilterFields(selectedTrigger);
    const isFormDirty: boolean = Object.keys(formState.dirtyFields || {})?.length > 0;

    useEffect(() => {
        if (automationType === 'ON_DEMAND') {
            setValue('triggerType', undefined);
        }
    }, [automationType, setValue]);

    useEffect(() => {
        if (isFormDirty) {
            resetField('companyFilters', { defaultValue: [] });
        }
    }, [selectedTrigger]);

    useEffect(() => {
        if (filteredFields.length && (!selectedAutomation || isFormDirty)) {
            remove();
            filteredFields.forEach((field) => {
                append({ filterField: field.filterField });
            });
        }
    }, [filteredFields]);

    const {
        AUTOMATION_SECTION_ADD_CONDITION
    } = useMultipleLocalizedText()

    const {
        status,
    } = useAutomationFormDataQuery({
        onSuccess: () => {},
    })

    const errorType = errors?.map((el: any) => el.type);

    const messagePartsToTranslate = useMemo(() => {
        return errors?.flatMap((error: any) =>
            (error.message.match(/(\b[A-Z_]+(?:[A-Z_]+)*\b)/g) || [])
                .map((part: string) => `AUTOMATION_FILTER_${part}`)
    );
    }, [errors]);

    const translations = useLocalizedTextArray(messagePartsToTranslate || []);

    const [errorMessages, setErrorMessages] = useState([]);

    const [globalErrors, setGlobalErrors] = useState([]);

    useEffect(() => {
        const newErrors = errors.map((error: any) => {
            let translatedMessage = error.message;
            (error.message.match(/(\b[A-Z_]+(?:[A-Z_]+)*\b)/g) || []).forEach((part: string, index: number) => {
                if (translations[index]) {
                    translatedMessage = translatedMessage.replace(part, translations[index]);
                }
            });
            return { ...error, translatedMessage };
        });
        setErrorMessages(newErrors.map((error: any) => {
            const index = companyFilters.findIndex((field: any) => field.filterField === error.field);
            return { ...error, index };
        }));
    }, [errors, companyFilters, translations]);

    useEffect(() => {
        setGlobalErrors(errorMessages.filter(error => error.index === -1))
    }, [errorMessages]);

    useEffect(() => {
        setErrors([]);
        setGlobalErrors([])
    }, [fields, setErrors]);

    if (status === 'loading') {
        return (
            <ConditionLoadingContainer>
                <CircularProgress size="32px" />
            </ConditionLoadingContainer>
        )
    }

    if (status === 'error') {
        return (
            <ConditionLoadingContainer>
                <LocalizedTypography color="error" text={LocalizedText.AUTOMATION_FORM_ERROR_CONTINUE} />
            </ConditionLoadingContainer>
        )
    }

    return (
        <>
            <div>
                {fields.map((field: any, index) => {
                    const errorIndex = errorMessages.findIndex((el: any) => el.index === index);
                    const hasError = errorIndex !== -1 && errorType.includes('FILTER_FIELD');
                    const errorMessage = hasError ? errorMessages[errorIndex].translatedMessage : null;

                    return (
                       <div  key={field.id}>
                            <AndContainer>
                                 {(fields.length > 1 && index > 0 && (
                                     <LocalizedTypography
                                         variant="body1"
                                         color={hasError ? '#D32F2F' : '#677074'}
                                         text={LocalizedText.AndLabel}
                                     />
                                ))}
                            </AndContainer>
                            <ConditionContainer>
                                <ConditionInput id={`automation-form-condition-field-${index}`} conditionIndex={index} error={hasError} errorMessage={errorMessage}>
                                    {(fields.length > 1 && (
                                        <div>
                                            <IconButton id={`automation-form-condition-field-${index}-delete-button`} onClick={() => {
                                                remove(index);
                                                setErrorMessages(errorMessages.filter((error, eIndex) => eIndex !== index))
                                            }}><DeleteIcon /></IconButton>
                                        </div>
                                    ))}
                                </ConditionInput>
                            </ConditionContainer>
                       </div>
                )})}
            </div>
            {globalErrors.map((error, index) => (
                <Typography key={index} color="error" sx={{fontSize: "12px", marginTop: "10px", maxWidth: "570px"}}>
                    {error.translatedMessage}
                </Typography>
            ))}
            <AddConditionContainer>
                <Button
                    id='automation-form-add-condition-button'
                    variant="outlined"
                    color="primary"
                    onClick={() =>
                        append({ filterField: '' })
                    }
                    disabled={fields.length >= 5}
                >
                    {AUTOMATION_SECTION_ADD_CONDITION}
                </Button>
            </AddConditionContainer>
        </>
    )
}

export default ConditionListSection
